import React from "react";
import { NavLink } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";
import {
  HomeIcon,
  LargeDollarIcon,
  MenuIconRequests,
  MenuIconAccount,
} from "../../Media/Icon/Icons/Icons";
import { LanguageSwitcher } from "../../OtherUtilities/LanguageSwitcher/LanguageSwitcher";
import tokens from "../../../shared/theme/tokens";
import { useNavItems, NavItem } from "./useNavItems";
import lockup from "../../../assets/images/brand/tenantport-lockup.svg";

const ICONS: Record<NavItem["key"], React.ReactNode> = {
  home: <HomeIcon />,
  rent: <LargeDollarIcon />,
  requests: <MenuIconRequests />,
  settings: <MenuIconAccount />,
};

/**
 * Desktop (>= md) sidebar for the authorized app shell. Same four
 * destinations as the mobile tab bar (config in useNavItems), plus the
 * brand lockup and the language switcher the legacy header used to hold.
 */
export const SideNav = () => {
  const navItems = useNavItems();

  return (
    <Stack
      component="nav"
      sx={{
        width: `${tokens.layout.sidebarWidth}px`,
        flexShrink: 0,
        height: "100%",
        backgroundColor: tokens.colors.surface,
        borderRight: `1px solid ${tokens.colors.border}`,
        px: 1.5,
        pt: 3,
        pb: 2,
      }}
    >
      <Box sx={{ px: 1.25, pb: 3.5 }}>
        <img
          src={lockup}
          alt="TenantPort"
          style={{ height: 34, maxWidth: "100%" }}
        />
      </Box>

      <Stack spacing={0.5} sx={{ flex: 1 }}>
        {navItems.map((item) => (
          <Box
            key={item.key}
            component={NavLink}
            to={item.urlPath}
            aria-disabled={item.disabled}
            onClick={(e: React.MouseEvent) =>
              item.disabled && e.preventDefault()
            }
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.5,
              py: 1.25,
              borderRadius: `${tokens.radius.sm}px`,
              color: tokens.colors.inkSecondary,
              opacity: item.disabled ? 0.45 : 1,
              cursor: item.disabled ? "default" : "pointer",
              "& svg": { width: 20, height: 20, flexShrink: 0 },
              "&:hover": {
                backgroundColor: tokens.colors.hover,
                color: tokens.colors.ink,
              },
              "&.active": {
                backgroundColor: tokens.colors.primarySoft,
                color: tokens.colors.primaryDeep,
                "& p": { fontWeight: 600 },
              },
            }}
          >
            {ICONS[item.key]}
            <Typography
              component="p"
              sx={{ fontSize: 14, fontWeight: 500, color: "inherit" }}
            >
              {item.title}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Box
        sx={{
          borderTop: `1px solid ${tokens.colors.border}`,
          pt: 1.5,
          px: 0.5,
        }}
      >
        <LanguageSwitcher isLeftLaigned />
      </Box>
    </Stack>
  );
};
